import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import CompanySelector from '@components/shared/CompanySelector';
import { havalandirmaTurleri } from '@mocks/materials/havalandirma';
import { mockCompanies } from '@mocks/firmalar';
import { calculateKDV, DEFAULT_KDV_RATE } from '@utils/kdv';
import KDVSummary from '../../components/KDVSummary';

export default function HavalandirmaModal({ isOpen, onClose, onSubmit, onMaterialSubmit, submitLabel, initialData = null, isLoading = false, insaatId }) {
    const isEdit = !!initialData;
    const [selectedCompany, setSelectedCompany] = useState(
        initialData?.supplierId ? { id: initialData.supplierId, name: initialData.tedarikci } : null
    );
    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: initialData || { siparisTarihi: new Date().toISOString().split('T')[0], tur: 'Split Klima', durum: 'bekliyor', kdvOrani: DEFAULT_KDV_RATE },
    });

    const watchedBirimFiyat = watch('birimFiyat');
    const watchedMiktar = watch('miktar');
    const watchedKdvOrani = watch('kdvOrani');

    const handleFormSubmit = (data) => {
        const result = calculateKDV({ unitPrice: data.birimFiyat, quantity: data.miktar, vatRate: data.kdvOrani });
        data.toplamFiyat = result.toplamHaric;
        data.kdvDahilToplam = result.toplamDahil;
        if (insaatId) data.insaatId = insaatId;
        data.tedarikci = selectedCompany?.name || data.tedarikci;
        data.supplierId = selectedCompany?.id || null;

        const enriched = {
            ...data,
            selectedCompany,
            calculatedTotal: data.kdvDahilToplam,
            autoDescription: `Havalandırma — ${data.miktar} adet ${data.tur}`,
        };

        if (onMaterialSubmit) {
            onMaterialSubmit(enriched);
        } else {
            onSubmit(enriched, isEdit);
        }
    };

    const handleClose = () => { reset(); onClose(); };
    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors";
    const errorClass = "border-error focus:ring-error/50";

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={isEdit ? 'Kayıt Düzenle' : 'Yeni Havalandırma / Klima'} size="lg"
            footer={<><Button variant="ghost" onClick={handleClose} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit(handleFormSubmit)} isLoading={isLoading}>{submitLabel || (isEdit ? 'Güncelle' : 'Kayıt Oluştur')}</Button></>}>
            <form className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CompanySelector value={selectedCompany} onChange={setSelectedCompany} companies={mockCompanies} label="Tedarikçi / Firma" />
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Sipariş Tarihi</label><input type="date" {...register('siparisTarihi')} className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Marka <span className="text-error">*</span></label>
                        <input type="text" {...register('marka', { required: true })} placeholder="Daikin, Mitsubishi..." className={`${inputClass} ${errors.marka ? errorClass : ''}`} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Model <span className="text-error">*</span></label>
                        <input type="text" {...register('model', { required: true })} placeholder="FTXM35R" className={`${inputClass} ${errors.model ? errorClass : ''}`} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Tür <span className="text-error">*</span></label>
                        <select {...register('tur', { required: true })} className={inputClass}>{havalandirmaTurleri.map(t => (<option key={t.key} value={t.key}>{t.label}</option>))}</select></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Kapasite</label>
                        <input type="text" {...register('kapasite')} placeholder="12.000 BTU / 400 m³/h" className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Miktar <span className="text-error">*</span></label>
                        <input type="number" {...register('miktar', { required: true, valueAsNumber: true })} placeholder="0" className={`${inputClass} ${errors.miktar ? errorClass : ''}`} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Birim Fiyat (₺)</label><input type="number" step="1" {...register('birimFiyat', { valueAsNumber: true })} placeholder="28000" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">KDV (%)</label><input type="number" step="1" {...register('kdvOrani', { valueAsNumber: true })} placeholder="20" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Durum</label><select {...register('durum')} className={inputClass}><option value="bekliyor">Bekliyor</option><option value="teslim">Teslim Edildi</option></select></div>
                </div>
                <KDVSummary unitPrice={watchedBirimFiyat} quantity={watchedMiktar} vatRate={watchedKdvOrani} />
                <div><label className="block text-sm font-medium text-text-primary mb-1.5">Not</label><textarea {...register('not')} rows={2} placeholder="Kayıt notu..." className={`${inputClass} resize-none`} /></div>
            </form>
        </Modal>
    );
}
